import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { smsTemplatesApi } from "@/api";
import type { SmsTemplate } from "@/types";
import { format } from "date-fns";
import { Plus, Save, Trash2, X, MessageSquare } from "lucide-react";

/** SMS segment count: 70 chars for 1st segment, 67 per additional */
function smsSegments(len: number): number {
  if (len <= 70) return 1;
  return Math.ceil(len / 67);
}

export default function SmsTemplates() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SmsTemplate | null>(null);
  const [isNew, setIsNew] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["sms-templates"],
    queryFn: () => smsTemplatesApi.list(),
  });

  const saveMutation = useMutation({
    mutationFn: (tpl: { id?: string; name: string; body: string }) => {
      if (tpl.id) return smsTemplatesApi.update(tpl.id, tpl);
      return smsTemplatesApi.create(tpl);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-templates"] });
      setEditing(null);
      setIsNew(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => smsTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-templates"] });
      setEditing(null);
    },
  });

  const handleNew = () => {
    setEditing({ id: "", name: "", body: "", createdAt: "", updatedAt: "" });
    setIsNew(true);
  };

  const handleEdit = async (id: string) => {
    const tpl = await smsTemplatesApi.get(id);
    setEditing(tpl);
    setIsNew(false);
  };

  const handleSave = () => {
    if (!editing || !editing.name.trim() || !editing.body.trim()) return;
    saveMutation.mutate({
      ...(isNew ? {} : { id: editing.id }),
      name: editing.name,
      body: editing.body,
    });
  };

  const bodyLen = editing?.body.length || 0;
  const segments = smsSegments(bodyLen);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">SMSテンプレート</h2>
        {!editing && (
          <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
          >
            <Plus size={16} />
            新規テンプレート
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4 max-w-2xl">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <label className="block text-xs font-medium text-slate-600 mb-1">テンプレート名</label>
            <input
              type="text"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="注文確認SMS"
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-600">本文</label>
              <span className={`text-xs ${bodyLen > 670 ? "text-red-600 font-bold" : "text-slate-400"}`}>
                {bodyLen}/670文字 ({segments}通分)
              </span>
            </div>
            <textarea
              value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              rows={6}
              maxLength={670}
              placeholder={"{{lastName}}様\n\nスフィアライトの新商品のご案内です。\n\n詳細: https://..."}
              className="w-full px-3 py-2 border rounded-md text-sm resize-y font-mono"
            />
            <p className="text-xs text-slate-400 mt-1">
              変数: {"{{lastName}}"}, {"{{firstName}}"}, {"{{company}}"} が使用可能
            </p>
          </div>

          {/* Preview */}
          <div className="bg-slate-100 p-4 rounded-lg">
            <h4 className="text-xs font-medium text-slate-500 mb-2">プレビュー</h4>
            <div className="bg-white rounded-2xl p-4 max-w-xs mx-auto shadow-sm border">
              <div className="bg-green-100 rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words">
                {editing.body
                  .replace(/\{\{lastName\}\}/g, "テスト")
                  .replace(/\{\{firstName\}\}/g, "太郎")
                  .replace(/\{\{company\}\}/g, "テスト株式会社")
                  || "(本文を入力してください)"}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending || !editing.name.trim() || !editing.body.trim() || bodyLen > 670}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              <Save size={16} />
              {saveMutation.isPending ? "保存中..." : "保存"}
            </button>
            {!isNew && (
              <button
                onClick={() => { if (confirm("このテンプレートを削除しますか？")) deleteMutation.mutate(editing.id); }}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
              >
                <Trash2 size={16} />
                削除
              </button>
            )}
            <button
              onClick={() => { setEditing(null); setIsNew(false); }}
              className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-slate-50"
            >
              <X size={16} />
              キャンセル
            </button>
          </div>
        </div>
      ) : isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : !data?.data.length ? (
        <div className="text-center py-12 text-slate-400">
          <MessageSquare size={40} className="mx-auto mb-2 opacity-40" />
          <p>SMSテンプレートがありません</p>
          <p className="text-sm mt-1">「新規テンプレート」ボタンから作成してください</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.data.map((tpl) => (
            <div
              key={tpl.id}
              onClick={() => handleEdit(tpl.id)}
              className="bg-white rounded-lg shadow-sm border hover:border-green-300 cursor-pointer transition-colors p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare size={16} className="text-green-500" />
                <h3 className="font-medium text-sm truncate">{tpl.name}</h3>
              </div>
              <p className="text-xs text-slate-500 line-clamp-3">{tpl.body}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-slate-400">{tpl.body.length}文字</span>
                <span className="text-xs text-slate-400">
                  {format(new Date(tpl.updatedAt), "yyyy/MM/dd")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
